import { useCallback, useMemo } from 'react';

import { ToggleState } from 'components/elements/Toggle/Toggle';
import { TranslationFunction } from 'hooks/useTypedTranslation';
import { skipContentSettingNames } from 'modules/listening/features/settings/settings';

import { BracesIcon, BracketsIcon, CitationIcon, FootersIcon, FormulasIcon, HeadersIcon, ParenthesesIcon, TablesIcon, UrlIcon } from '../../icons';
import FootNotesIcon from '../../icons/FootNotesIcon';

interface AutoSkipToggleSetting {
  description: string;
  icon: JSX.Element;
  key: (typeof skipContentSettingNames)[number];
  separator?: boolean;
  state: ToggleState;
  title: string;
}

interface UseAutoSkipToggleSettingsParams {
  t: TranslationFunction;
  autoSkipContentUnlocked: boolean;
  skipContentSettings: Record<string, boolean>;
}

export const useAutoSkipToggleSettings = ({ t, autoSkipContentUnlocked, skipContentSettings }: UseAutoSkipToggleSettingsParams): AutoSkipToggleSetting[] => {
  const getIndividualSkipContentSettingsState = useCallback(
    (key: (typeof skipContentSettingNames)[number]): ToggleState => {
      if (!autoSkipContentUnlocked) {
        if (key === 'headers' || key === 'footers' || key === 'urls') {
          return skipContentSettings[key] ? 'checked' : 'default';
        }

        return 'locked';
      }

      return skipContentSettings[key] ? 'checked' : 'default';
    },
    [autoSkipContentUnlocked, skipContentSettings]
  );

  const autoSkipToggleSettings = useMemo(
    (): AutoSkipToggleSetting[] => [
      {
        key: 'headers' as const,
        icon: <HeadersIcon />,
        title: t('Headers'),
        description: t('Title, Author and more'),
        state: getIndividualSkipContentSettingsState('headers')
      },
      {
        key: 'footers' as const,
        icon: <FootersIcon />,
        title: t('Footers'),
        description: t('Page number and more'),
        state: getIndividualSkipContentSettingsState('footers')
      },
      {
        key: 'footnotes' as const,
        icon: <FootNotesIcon />,
        title: t('Footnotes'),
        description: t('1. René Stauffer   2. Simon Graf'),
        state: getIndividualSkipContentSettingsState('footnotes'),
        separator: true
      },
      {
        key: 'tables' as const,
        icon: <TablesIcon />,
        title: t('Tables'),
        description: t('| ABC | 123 |'),
        state: getIndividualSkipContentSettingsState('tables')
      },
      {
        key: 'formulas' as const,
        icon: <FormulasIcon />,
        title: t('Formulas'),
        description: t('f = v / λ'),
        state: getIndividualSkipContentSettingsState('formulas')
      },
      {
        key: 'citations' as const,
        icon: <CitationIcon />,
        title: t('Citations'),
        description: t('(Author, 1995)'),
        state: getIndividualSkipContentSettingsState('citations')
      },
      {
        key: 'urls' as const,
        icon: <UrlIcon />,
        title: t('URLs'),
        description: t('site.com/...'),
        state: getIndividualSkipContentSettingsState('urls'),
        separator: true
      },
      {
        key: 'parentheses' as const,
        icon: <ParenthesesIcon />,
        title: t('Parentheses'),
        description: t('(some text)'),
        state: getIndividualSkipContentSettingsState('parentheses')
      },
      {
        key: 'brackets' as const,
        icon: <BracketsIcon />,
        title: t('Brackets'),
        description: t('[some text]'),
        state: getIndividualSkipContentSettingsState('brackets')
      },
      {
        key: 'braces' as const,
        icon: <BracesIcon />,
        title: t('Braces'),
        description: t('{xyz123}'),
        state: getIndividualSkipContentSettingsState('braces'),
        separator: true
      }
    ],
    [t, getIndividualSkipContentSettingsState]
  );

  return autoSkipToggleSettings;
};

export type { AutoSkipToggleSetting };
